import { FC, useEffect, useState } from "react"
import { HeaderHeight } from "../molecules/Header"
import { makeStyles, Typography } from "@material-ui/core"
import { colors } from "../../utils/theme"
import { IconButton } from "../atoms/button/Button"
import AddStaffDialog from "../molecules/AddStaffDialog"
import { useUser } from "../../hooks/useUser"
import PlusIcon from "../atoms/PlusIcon"
import StaffTable from "../molecules/StaffTable"
import { useOrganization } from "../../hooks/useOrganization"
import StaffOverview from "../molecules/StaffOverview"
import TechniqueProfile from "../molecules/TechniqueProfile"
import ClientProgress from "../molecules/ClientProgress"
import { ComponentHeaderHeight, Role } from "../../utils/constants"
import SupervisorTable from "../molecules/SupervisorTable"
import { useNavigation } from "../../hooks/useNavigation"
import { useSession } from "../../hooks/useSession"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import {
  GetUserOrganizationAdminsDocument,
  GetUserOrganizationAdminsQuery,
  GetUserOrganizationAdminsQueryVariables,
} from "../../generated/graphql"

const useStyles = makeStyles((theme) => ({
  myOrg: {
    paddingTop: HeaderHeight,
    backgroundColor: colors.neutral.white,
    flex: 1,
  },
  header: {
    height: ComponentHeaderHeight,
    maxWidth: 1440,
    margin: "0 auto",
  },
  line: {
    borderBottom: `1px solid ${colors.neutral.clay}`,
  },
  contentContainer: {
    display: "flex",
    justifyContent: "center",
    flex: 1,
    width: "100%",
  },
  content: {
    maxWidth: 1440,
    padding: theme.spacing(2, 3),
  },
  title: {
    color: colors.neutral.denim,
  },
  noDataFirstLine: {
    fontWeight: 700,
    marginBottom: 6,
  },
  noStaff: {
    textAlign: "center",
    paddingTop: 50,
    paddingBottom: 25,
  },
  addStaff: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  addStaffButton: {
    width: 279,
  },
  navigation: {
    height: 92,
    width: "100%",
    display: "flex",
    alignItems: "center",
    padding: 0,
    margin: 0,
    border: 0,
    backgroundColor: "transparent",
    paddingLeft: theme.spacing(3),
    outline: "none",
  },
  staffLabel: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  staffTitle: {
    color: colors.neutral.denim,
    marginRight: theme.spacing(2),
  },
  techniquesProgressContainer: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr",
    gap: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  supervisorLabel: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(3),
  },
}))

const MyOrganization: FC = () => {
  const classes = useStyles()
  const { user } = useUser()
  const { session } = useSession()
  const [openAddDialog, setOpenAddDialog] = useState<boolean>(false)
  const {
    staffTableData,
    staffOverviewData,
    techniqueProfileData,
    clientProgressData,
    refetchMyOrg,
    supervisorTableData,
  } = useOrganization()

  const { toDashboard, toPractitioners } = useNavigation()
  const graphqlClient = useGraphQLClient()
  useEffect(() => {
    const fetchOrgAdmins = async () => {
      if (user?.id) {
        const getUserOrgAdminsResponse = await graphqlClient.request<
          GetUserOrganizationAdminsQuery,
          GetUserOrganizationAdminsQueryVariables
        >(GetUserOrganizationAdminsDocument, {
          id: user?.id,
        })
        const orgAdmins =
          getUserOrgAdminsResponse?.users_by_pk?.organization_admins
        if (orgAdmins && orgAdmins.length === 0) {
          session?.role === Role.WEB_ROLE ? toPractitioners() : toDashboard()
        }
      }
    }
    fetchOrgAdmins()
  }, [user])

  return (
    <>
      <div className={classes.myOrg}>
        <div className={classes.header}>
          <div className={classes.navigation}>
            <Typography variant="h2" className={classes.title}>
              {user?.organizations?.[0].name}
            </Typography>
          </div>
        </div>
        <div className={classes.line} />
        <div className={classes.contentContainer}>
          <div className={classes.content}>
            <div className={classes.techniquesProgressContainer}>
              <TechniqueProfile techniqueProfileData={techniqueProfileData} />
              <ClientProgress clientProgressData={clientProgressData} />
            </div>
            <StaffOverview staffOverviewData={staffOverviewData} />
            <div className={classes.staffLabel}>
              <Typography variant="h2" className={classes.staffTitle}>
                Staff
              </Typography>
              <IconButton onClick={() => setOpenAddDialog(true)}>
                <PlusIcon />
              </IconButton>
            </div>
            <StaffTable staffData={staffTableData} />
            <div className={classes.supervisorLabel}>
              <Typography variant="h2" className={classes.staffTitle}>
                Supervisors
              </Typography>
            </div>
            <SupervisorTable supervisorData={supervisorTableData} />
          </div>
        </div>
      </div>

      <AddStaffDialog
        open={openAddDialog}
        onClose={() => {
          setOpenAddDialog(false)
          refetchMyOrg()
        }}
      />
    </>
  )
}

export default MyOrganization
